import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, Users, Star, MapPin, Building2, Award,
  Linkedin, Github, Globe, Mail, X, CheckCircle2,
} from 'lucide-react';
import API_BASE from '@/lib/API_BASE';

const easeOut = [0.22, 1, 0.36, 1];

const gridVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeOut } },
};

const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const socialsOf = (t) => [
  { key: 'linkedin', href: t.linkedin_url, icon: Linkedin, label: 'LinkedIn' },
  { key: 'github',   href: t.github_url,   icon: Github,   label: 'GitHub' },
  { key: 'website',  href: t.website_url,  icon: Globe,    label: 'Website' },
  { key: 'email',    href: t.email ? `mailto:${t.email}` : '', icon: Mail, label: 'Email' },
].filter(s => s.href);

export default function TrainersSection() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;

    axios.get(`${API_BASE}/get-trainers.php`)
      .then(res => {
        if (cancelled) return;
        if (res.data?.success) setTrainers(res.data.data || []);
      })
      .catch(() => { /* section stays hidden if the fetch fails */ })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  // Lock background scroll while the profile modal is open
  useEffect(() => {
    if (!selected) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [selected]);

  // Nothing to show yet — render nothing rather than an empty section
  if (loading || trainers.length === 0) return null;

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
            Our Trainers
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Learn From Industry Experts</h2>
          <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Every XceedEdge program is delivered by working professionals who bring years of
            hands-on experience from the field into the classroom.
          </p>
        </div>

        <motion.div
          variants={gridVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {trainers.map((t) => (
            <motion.article
              key={t.id}
              variants={cardVariants}
              whileHover={{ y: -6, transition: { type: 'spring', stiffness: 300, damping: 22 } }}
              onClick={() => setSelected(t)}
              className="group relative flex cursor-pointer flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-[box-shadow,border-color] duration-500 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/10"
            >
              {/* Gradient banner */}
              <div className="relative h-20 overflow-hidden bg-gradient-to-br from-primary/20 via-primary/10 to-accent/20">
                <div className="absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
                <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
              </div>

              <div className="-mt-12 flex flex-grow flex-col px-6 pb-6">
                {/* Avatar */}
                <div className="relative mx-auto">
                  <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-primary to-accent opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-70" />
                  {t.photo_url ? (
                    <img
                      src={t.photo_url}
                      alt={t.name}
                      loading="lazy"
                      className="relative h-24 w-24 rounded-full object-cover ring-4 ring-card transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent ring-4 ring-card transition-transform duration-500 group-hover:scale-105">
                      <span className="font-heading text-2xl font-bold tracking-wider text-white">
                        {initialsOf(t.name) || 'XE'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Identity */}
                <div className="mt-4 text-center">
                  <h3 className="font-heading text-lg font-bold leading-tight">{t.name}</h3>
                  {t.designation && (
                    <p className="mt-1 text-sm text-muted-foreground">{t.designation}</p>
                  )}
                  {t.domain && (
                    <span className="mt-2.5 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                      {t.domain}
                    </span>
                  )}
                </div>

                {/* Company / location */}
                {(t.company || t.location) && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {t.company && (
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3.5 w-3.5" /> {t.company}
                      </span>
                    )}
                    {t.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> {t.location}
                      </span>
                    )}
                  </div>
                )}

                {/* Stat strip */}
                <TrainerStats trainer={t} className="mt-4" />

                {/* Bio */}
                {t.bio && (
                  <p className="mt-4 flex-grow text-center text-sm leading-relaxed text-muted-foreground line-clamp-3">
                    {t.bio}
                  </p>
                )}

                {/* Expertise */}
                {Array.isArray(t.expertise) && t.expertise.length > 0 && (
                  <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                    {t.expertise.slice(0, 4).map((skill, i) => (
                      <span
                        key={i}
                        className="rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-foreground/70 transition-colors duration-500 group-hover:bg-primary/5"
                      >
                        {skill}
                      </span>
                    ))}
                    {t.expertise.length > 4 && (
                      <span className="rounded-md px-2 py-1 text-[11px] font-medium text-primary">
                        +{t.expertise.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Footer: socials + view profile */}
                <div className="mt-5 flex items-center justify-between border-t border-border/50 pt-4">
                  <div className="flex items-center gap-1.5">
                    {socialsOf(t).map(({ key, href, icon: Icon, label }) => (
                      <a
                        key={key}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${t.name} on ${label}`}
                        onClick={(e) => e.stopPropagation()}
                        className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary"
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    View profile →
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>

      {/* Full profile modal */}
      <AnimatePresence>
        {selected && (
          <TrainerModal trainer={selected} onClose={() => setSelected(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}

function TrainerStats({ trainer: t, className = '' }) {
  const stats = [
    t.experience_years ? { icon: Briefcase, value: `${t.experience_years}+`, label: 'Years' } : null,
    t.students_trained ? { icon: Users, value: `${t.students_trained}+`, label: 'Trained' } : null,
    t.rating ? { icon: Star, value: Number(t.rating).toFixed(1), label: 'Rating' } : null,
  ].filter(Boolean);

  if (stats.length === 0) return null;

  return (
    <div className={`grid gap-2 rounded-xl bg-muted/60 p-3 ${className}`} style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
      {stats.map(({ icon: Icon, value, label }, i) => (
        <div key={i} className="text-center">
          <div className="flex items-center justify-center gap-1 font-heading text-sm font-bold text-foreground">
            <Icon className="h-3.5 w-3.5 text-primary" /> {value}
          </div>
          <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>
      ))}
    </div>
  );
}

function TrainerModal({ trainer: t, onClose }) {
  const socials = socialsOf(t);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${t.name} profile`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(11,11,11,0.6)' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.28, ease: easeOut }}
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
      >
        {/* Banner */}
        <div className="relative h-24 shrink-0 bg-gradient-to-br from-primary/25 via-primary/10 to-accent/25">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="absolute right-3 top-3 rounded-lg bg-card/80 p-2 text-foreground/70 transition hover:bg-card hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="-mt-12 flex-1 overflow-y-auto px-6 pb-6 sm:px-8">
          {/* Header */}
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-end sm:gap-5 sm:text-left">
            {t.photo_url ? (
              <img
                src={t.photo_url}
                alt={t.name}
                className="h-24 w-24 shrink-0 rounded-full object-cover ring-4 ring-card"
              />
            ) : (
              <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent ring-4 ring-card">
                <span className="font-heading text-2xl font-bold tracking-wider text-white">
                  {initialsOf(t.name) || 'XE'}
                </span>
              </div>
            )}
            <div className="mt-3 sm:mt-0 sm:pb-2">
              <h3 className="font-heading text-2xl font-bold leading-tight">{t.name}</h3>
              {t.designation && <p className="mt-1 text-sm text-muted-foreground">{t.designation}</p>}
              <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                {t.domain && (
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    {t.domain}
                  </span>
                )}
                {t.company && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Building2 className="h-3.5 w-3.5" /> {t.company}
                  </span>
                )}
                {t.location && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {t.location}
                  </span>
                )}
              </div>
            </div>
          </div>

          <TrainerStats trainer={t} className="mt-6" />

          {t.bio && (
            <div className="mt-6">
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground/80">About</h4>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{t.bio}</p>
            </div>
          )}

          {Array.isArray(t.expertise) && t.expertise.length > 0 && (
            <div className="mt-6">
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground/80">
                Areas of Expertise
              </h4>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {t.expertise.map((skill, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <span className="text-xs font-medium leading-snug text-foreground/80">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(t.certifications) && t.certifications.length > 0 && (
            <div className="mt-6">
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-foreground/80">
                Certifications
              </h4>
              <div className="mt-3 flex flex-wrap gap-2">
                {t.certifications.map((cert, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary"
                  >
                    <Award className="h-3.5 w-3.5" /> {cert}
                  </span>
                ))}
              </div>
            </div>
          )}

          {socials.length > 0 && (
            <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-border/50 pt-5">
              {socials.map(({ key, href, icon: Icon, label }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium text-foreground/70 transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                >
                  <Icon className="h-4 w-4" /> {label}
                </a>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
